import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  CircleAlert,
  Copy,
  Eye,
  EyeOff,
  Landmark,
  Lock,
  MapPinned,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Store,
  TimerReset
} from 'lucide-react';
import StoreLocationEditor from '../../account/components/StoreLocationEditor';
import { GoogleMapsProvider } from '../../../shared/providers/GoogleMapsProvider';
import { INDIA_STATES } from '../../../shared/constants/indiaLocations';
import {
  AUTH_MALLS,
  AUTH_MALLS_REGISTER,
  AUTH_PINCODE_LOOKUP,
  AUTH_STORE_REGISTRATION_DRAFT,
  AUTH_STORE_REGISTRATION_DRAFT_ADDRESS_AND_VENUE_STEP,
  AUTH_STORE_REGISTRATION_DRAFT_LOCATION_STEP,
  AUTH_STORE_REGISTRATION_DRAFT_STORE_CODE_GENERATE,
  AUTH_STORE_REGISTRATION_DRAFT_STORE_CODE_REVIEW_STEP,
  AUTH_STORE_REGISTRATION_DRAFT_STORE_DETAILS_STEP,
  AUTH_STORE_REGISTRATION_DRAFT_SUBMIT,
  AUTH_STORE_REGISTRATION_DRAFTS
} from '../../../shared/constants/endpoints';
import './StoreRegistrationOnboardingPage.css';

const MARKETPLACE_OPTIONS = [
  {
    value: 'ZOPEO_MARKETPLACE',
    label: 'Zopeo Marketplace (default)',
    helper: 'Helps customers find you in the right hub.'
  }
];

const CATEGORY_OPTIONS = [
  {
    value: 'Salon & Spa',
    description: 'Hair, beauty & wellness',
    clientType: 'SALON'
  },
  {
    value: 'Restaurant / QSR',
    description: 'Food & beverage outlets',
    clientType: 'FOOD_COURT'
  },
  {
    value: 'Retail & Apparel',
    description: 'Fashion & merchandise',
    clientType: 'RETAIL'
  },
  {
    value: 'Clinic & Pharmacy',
    description: 'Health & medical',
    clientType: 'CLINIC'
  },
  {
    value: 'Sports & Fitness',
    description: 'Gyms, studios & sports',
    clientType: 'SPORTS'
  },
  {
    value: 'Wellness',
    description: 'Spa, massage & wellbeing',
    clientType: 'WELLNESS'
  },
  {
    value: 'Services',
    description: 'Repairs, bookings & more',
    clientType: 'RETAIL'
  },
  {
    value: 'Other',
    description: 'Something else entirely',
    clientType: 'RETAIL'
  }
];

const SHOPPING_CENTRE_MODE = {
  STANDALONE: 'STANDALONE',
  LINKED: 'LINKED',
  CUSTOM: 'CUSTOM'
};

const STEP_DEFINITIONS = [
  {
    id: 'location',
    number: 1,
    progressLabel: 'Step 1 of 4',
    label: 'Location',
    caption: 'State & region',
    pill: 'STEP 1 · LOCATION',
    title: 'Where does your store operate?',
    description:
      "Choose the state your store trades in. This tailors taxes, delivery zones and storefront discovery. You'll pin your exact address in a later step."
  },
  {
    id: 'store-details',
    number: 2,
    progressLabel: 'Step 2 of 4',
    label: 'Store details',
    caption: 'Business & contact',
    pill: 'STEP 2 · BUSINESS',
    title: 'Tell us about your business',
    description:
      'This information appears on your storefront, invoices and customer notifications. You can refine it later in settings.'
  },
  {
    id: 'address-and-venue',
    number: 3,
    progressLabel: 'Step 3 of 4',
    label: 'Address & venue',
    caption: 'Pin & shopping centre',
    pill: 'STEP 3 · LOCATION',
    title: 'Pin your storefront',
    description:
      'An accurate address and map marker power delivery routing, in-store pickup and local discovery. If you trade inside a shopping centre, you can link it below.'
  },
  {
    id: 'store-code-review',
    number: 4,
    progressLabel: 'Step 4 of 4',
    label: 'Store code & review',
    caption: 'Confirm & create',
    pill: 'STEP 4 · FINISH',
    title: 'Generate your store code & review',
    description:
      'Your store code is the unique key your team uses to sign in. Review everything below, then create your store.'
  },
  {
    id: 'complete',
    number: 4,
    progressLabel: 'Done',
    label: 'Complete',
    caption: 'Submitted',
    pill: 'DONE',
    title: 'Registration submitted',
    description:
      'Your store is now queued for approval. Save the generated store code somewhere safe and watch for the approval email before signing in.'
  }
];

const DEFAULT_LOCATION = {
  state: '',
  marketplaceKey: MARKETPLACE_OPTIONS[0].value
};

const DEFAULT_DETAILS = {
  storeName: '',
  businessCategory: '',
  clientType: '',
  email: '',
  phone: '',
  phoneType: 'mobile',
  chargesTaxSeparately: false
};

const DEFAULT_ADDRESS = {
  city: '',
  area: '',
  pincode: '',
  addressLine: '',
  latitude: null,
  longitude: null,
  shoppingCentreMode: SHOPPING_CENTRE_MODE.STANDALONE,
  shoppingCentreUuid: '',
  shoppingCentreName: '',
  floorOrUnit: ''
};

const DEFAULT_REVIEW = {
  confirmed: false
};

const DEFAULT_CUSTOM_SHOPPING_CENTRE = {
  name: '',
  description: ''
};

const DEFAULT_PASSWORD_MODAL = {
  open: false,
  password: '',
  confirmPassword: ''
};

const DEFAULT_SUMMARY = {
  initials: 'TG',
  storeName: 'Your store name',
  category: 'Business type',
  location: '—',
  state: '—',
  storeCode: '—',
  pincode: '—'
};

const getStepFromRoute = (route) => {
  if (route === '/register') {
    return { stepId: 'location', draftUuid: null };
  }

  const match = route.match(/^\/register\/([^/]+)\/(location|store-details|address-and-venue|store-code-review|complete)$/);
  if (!match) {
    return { stepId: 'location', draftUuid: null };
  }

  return {
    draftUuid: match[1],
    stepId: match[2]
  };
};

const getStepHref = (stepId, draftUuid) => {
  if (!draftUuid) {
    return '/register';
  }

  return `/register/${draftUuid}/${stepId}`;
};

const unwrapApiData = (payload) => payload?.data ?? payload ?? null;

const extractFieldErrors = (error) => error?.data?.data?.fieldErrors
  || error?.data?.fieldErrors
  || {};

const normalizeText = (value) => String(value || '').trim();
const normalizeDigits = (value) => String(value || '').replace(/\D/g, '');
const serializePayload = (payload) => JSON.stringify(payload);

const isEmailValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeText(value));
const isPhoneValid = (value) => /^\d{10,15}$/.test(normalizeDigits(value));
const isPincodeValidFormat = (value) => /^\d{6}$/.test(normalizeDigits(value));

const validatePassword = (password, confirmPassword) => {
  if (!password || password.length < 10) {
    return 'Password must be at least 10 characters';
  }
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }
  if (!/\d/.test(password)) {
    return 'Password must contain at least one digit';
  }
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(password)) {
    return 'Password must contain at least one special character';
  }
  if (password !== confirmPassword) {
    return 'Passwords do not match';
  }

  return '';
};

const buildSummary = (draftData, locationForm, detailsForm, addressForm) => {
  const storeName = draftData?.summary?.storeName || detailsForm.storeName || DEFAULT_SUMMARY.storeName;
  const category = draftData?.summary?.categoryLabel || detailsForm.businessCategory || DEFAULT_SUMMARY.category;
  const location = draftData?.summary?.locationLabel
    || [addressForm.area, addressForm.city].filter(Boolean).join(', ')
    || DEFAULT_SUMMARY.location;
  const state = draftData?.summary?.state || locationForm.state || DEFAULT_SUMMARY.state;
  const storeCode = draftData?.summary?.storeCode || draftData?.reservedStoreCode || DEFAULT_SUMMARY.storeCode;
  const pincode = draftData?.summary?.pincode || addressForm.pincode || DEFAULT_SUMMARY.pincode;
  const initials = storeName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || DEFAULT_SUMMARY.initials;

  return {
    initials,
    storeName,
    category,
    location,
    state,
    storeCode,
    pincode
  };
};

const stepIndexLookup = STEP_DEFINITIONS.reduce((accumulator, step, index) => {
  accumulator[step.id] = index;
  return accumulator;
}, {});

const ReviewRow = ({ label, value }) => (
  <div className="store-registration-review-row">
    <dt>{label}</dt>
    <dd>{value || '—'}</dd>
  </div>
);

const ReviewSection = ({
  title,
  onEdit,
  children
}) => (
  <section className="store-registration-review-section">
    <header className="store-registration-review-section-header">
      <h3>{title}</h3>
      <button type="button" onClick={onEdit}>Edit</button>
    </header>
    <dl className="store-registration-review-grid">
      {children}
    </dl>
  </section>
);

const PasswordModal = ({
  open,
  values,
  onChange,
  onClose,
  onSubmit,
  error,
  submitting
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="store-registration-modal-backdrop" role="presentation">
      <div className="store-registration-modal" role="dialog" aria-modal="true" aria-labelledby="store-registration-password-title">
        <div className="store-registration-modal-header">
          <div>
            <p className="store-registration-modal-eyebrow">Final confirmation</p>
            <h2 id="store-registration-password-title">Set the sign-in password</h2>
          </div>
          <button
            type="button"
            className="store-registration-modal-close"
            onClick={onClose}
            aria-label="Close password dialog"
          >
            ×
          </button>
        </div>
        <p className="store-registration-modal-copy">
          We only collect the password at the final step so draft autosave never stores password state.
        </p>
        <div className="store-registration-grid store-registration-grid--single">
          <div className="store-registration-field">
            <label htmlFor="store-registration-password">Password</label>
            <div className="store-registration-input">
              <Lock size={18} aria-hidden="true" />
              <input
                id="store-registration-password"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                onChange={(event) => onChange('password', event.target.value)}
                placeholder="Enter a strong password"
              />
              <button
                type="button"
                className="store-registration-input-action"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
              </button>
            </div>
          </div>
          <div className="store-registration-field">
            <label htmlFor="store-registration-confirm-password">Confirm password</label>
            <div className="store-registration-input">
              <Lock size={18} aria-hidden="true" />
              <input
                id="store-registration-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={values.confirmPassword}
                onChange={(event) => onChange('confirmPassword', event.target.value)}
                placeholder="Repeat the same password"
              />
              <button
                type="button"
                className="store-registration-input-action"
                onClick={() => setShowConfirmPassword((current) => !current)}
                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
              >
                {showConfirmPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
              </button>
            </div>
            <p className="store-registration-field-hint">
              Use at least 10 characters with an uppercase letter, digit, and special character.
            </p>
          </div>
        </div>
        {error ? (
          <div className="store-registration-inline-error store-registration-inline-error--standalone" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        ) : null}
        <div className="store-registration-modal-actions">
          <button type="button" className="store-registration-button store-registration-button--ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="store-registration-button store-registration-button--primary" onClick={onSubmit} disabled={submitting}>
            <span>{submitting ? 'Creating store...' : 'Create store'}</span>
            {!submitting ? <ArrowRight size={18} aria-hidden="true" /> : null}
          </button>
        </div>
      </div>
    </div>
  );
};

const LocationStepPanel = ({
  form,
  fieldErrors,
  onChange
}) => (
  <div className="store-registration-section-block">
    <div className="store-registration-grid store-registration-grid--two">
      <div className="store-registration-field">
        <label htmlFor="store-registration-state">
          STATE <span aria-hidden="true">*</span>
        </label>
        <div className={`store-registration-input store-registration-input--select${fieldErrors.state ? ' is-error' : ''}`}>
          <Landmark size={18} aria-hidden="true" />
          <select
            id="store-registration-state"
            value={form.state}
            onChange={(event) => onChange('state', event.target.value)}
          >
            <option value="">Select state</option>
            {INDIA_STATES.map((stateName) => (
              <option key={stateName} value={stateName}>
                {stateName}
              </option>
            ))}
          </select>
          <ChevronDown size={18} aria-hidden="true" />
        </div>
        {fieldErrors.state ? (
          <div className="store-registration-inline-error" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{fieldErrors.state}</span>
          </div>
        ) : null}
      </div>

      <div className="store-registration-field">
        <label htmlFor="store-registration-marketplace">MARKETPLACE <span className="store-registration-optional">(optional)</span></label>
        <div className="store-registration-input store-registration-input--select">
          <Store size={18} aria-hidden="true" />
          <select
            id="store-registration-marketplace"
            value={form.marketplaceKey}
            onChange={(event) => onChange('marketplaceKey', event.target.value)}
          >
            {MARKETPLACE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown size={18} aria-hidden="true" />
        </div>
        <p className="store-registration-field-hint">
          {MARKETPLACE_OPTIONS[0].helper}
        </p>
      </div>
    </div>
  </div>
);

const StoreDetailsStepPanel = ({
  form,
  fieldErrors,
  onFieldChange,
  onCategorySelect
}) => (
  <div className="store-registration-section-stack">
    <section className="store-registration-section-block">
      <div className="store-registration-field">
        <label htmlFor="store-registration-store-name">
          BUSINESS NAME <span aria-hidden="true">*</span>
        </label>
        <div className={`store-registration-input${fieldErrors.storeName ? ' is-error' : ''}`}>
          <Store size={18} aria-hidden="true" />
          <input
            id="store-registration-store-name"
            type="text"
            value={form.storeName}
            onChange={(event) => onFieldChange('storeName', event.target.value)}
            placeholder="Your public brand name"
          />
          {!fieldErrors.storeName && form.storeName ? <Check size={18} aria-hidden="true" /> : null}
        </div>
        <p className="store-registration-field-hint">Your public-facing brand name as customers will see it.</p>
        {fieldErrors.storeName ? (
          <div className="store-registration-inline-error" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{fieldErrors.storeName}</span>
          </div>
        ) : null}
      </div>
    </section>

    <section className="store-registration-section-block">
      <div className="store-registration-section-heading">
        <h3>Business category</h3>
        <p>Sets the right templates, catalogue fields and tax rules for your storefront.</p>
      </div>
      <div className="store-registration-category-grid">
        {CATEGORY_OPTIONS.map((option) => {
          const selected = form.businessCategory === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={`store-registration-category-card${selected ? ' is-selected' : ''}`}
              onClick={() => onCategorySelect(option)}
            >
              <div className="store-registration-category-icon">
                <Sparkles size={18} aria-hidden="true" />
              </div>
              <div className="store-registration-category-copy">
                <strong>{option.value}</strong>
                <span>{option.description}</span>
              </div>
              <span className="store-registration-category-radio" aria-hidden="true" />
            </button>
          );
        })}
      </div>
      {fieldErrors.businessCategory ? (
        <div className="store-registration-inline-error" role="alert">
          <CircleAlert size={16} aria-hidden="true" />
          <span>{fieldErrors.businessCategory}</span>
        </div>
      ) : null}
    </section>

    <section className="store-registration-section-block">
      <div className="store-registration-section-heading">
        <h3>Contact</h3>
        <p>We’ll use these to verify your store and send operational alerts.</p>
      </div>
      <div className="store-registration-grid store-registration-grid--two">
        <div className="store-registration-field">
          <label htmlFor="store-registration-email">
            EMAIL <span aria-hidden="true">*</span>
          </label>
          <div className={`store-registration-input${fieldErrors.email ? ' is-error' : ''}`}>
            <ShieldCheck size={18} aria-hidden="true" />
            <input
              id="store-registration-email"
              type="email"
              value={form.email}
              onChange={(event) => onFieldChange('email', event.target.value)}
              placeholder="store@yourbrand.com"
            />
          </div>
          {fieldErrors.email ? (
            <div className="store-registration-inline-error" role="alert">
              <CircleAlert size={16} aria-hidden="true" />
              <span>{fieldErrors.email}</span>
            </div>
          ) : null}
        </div>

        <div className="store-registration-field">
          <label htmlFor="store-registration-phone">
            PHONE <span aria-hidden="true">*</span>
          </label>
          <div className={`store-registration-input store-registration-input--phone${fieldErrors.phone ? ' is-error' : ''}`}>
            <span className="store-registration-phone-prefix">+91</span>
            <input
              id="store-registration-phone"
              type="tel"
              value={form.phone}
              onChange={(event) => onFieldChange('phone', event.target.value)}
              placeholder="98765 43210"
            />
          </div>
          {fieldErrors.phone ? (
            <div className="store-registration-inline-error" role="alert">
              <CircleAlert size={16} aria-hidden="true" />
              <span>{fieldErrors.phone}</span>
            </div>
          ) : null}
        </div>
      </div>
    </section>

    <section className="store-registration-section-block">
      <div className="store-registration-section-heading">
        <h3>Taxes at billing</h3>
        <p>Controls how taxes appear on your customers’ appointment bills.</p>
      </div>
      <button
        type="button"
        className={`store-registration-tax-toggle${form.chargesTaxSeparately ? ' is-on' : ''}`}
        role="switch"
        aria-checked={Boolean(form.chargesTaxSeparately)}
        onClick={() => onFieldChange('chargesTaxSeparately', !form.chargesTaxSeparately)}
      >
        <span className="store-registration-tax-toggle-copy">
          <strong>Charge taxes separately at billing</strong>
          <span>
            Turn on if your listed prices are before tax and you add GST at billing. Customers then see a
            “taxes charged at store” note on their bill. Leave it off if your prices already include all taxes.
          </span>
        </span>
        <span className="store-registration-tax-switch" aria-hidden="true">
          <span className="store-registration-tax-switch-thumb" />
        </span>
      </button>
    </section>
  </div>
);

const AddressAndVenueStepPanel = ({
  locationForm,
  form,
  fieldErrors,
  pincodeStatus,
  malls,
  mallsLoading,
  customShoppingCentre,
  shoppingCentreSaving,
  mapValidation,
  onFieldChange,
  onCustomShoppingCentreChange,
  onRegisterCustomShoppingCentre
}) => {
  const usesShoppingCentre = form.shoppingCentreMode !== SHOPPING_CENTRE_MODE.STANDALONE;

  return (
    <div className="store-registration-section-stack">
      <section className="store-registration-section-block">
        <div className="store-registration-grid store-registration-grid--two">
          <div className="store-registration-field">
            <label htmlFor="store-registration-city">
              CITY <span aria-hidden="true">*</span>
            </label>
            <div className={`store-registration-input${fieldErrors.city ? ' is-error' : ''}`}>
              <Landmark size={18} aria-hidden="true" />
              <input
                id="store-registration-city"
                type="text"
                value={form.city}
                onChange={(event) => onFieldChange('city', event.target.value)}
                placeholder="Hyderabad"
              />
            </div>
            {fieldErrors.city ? (
              <div className="store-registration-inline-error" role="alert">
                <CircleAlert size={16} aria-hidden="true" />
                <span>{fieldErrors.city}</span>
              </div>
            ) : null}
          </div>

          <div className="store-registration-field">
            <label htmlFor="store-registration-area">
              AREA / LOCALITY <span aria-hidden="true">*</span>
            </label>
            <div className={`store-registration-input${fieldErrors.area ? ' is-error' : ''}`}>
              <MapPinned size={18} aria-hidden="true" />
              <input
                id="store-registration-area"
                type="text"
                value={form.area}
                onChange={(event) => onFieldChange('area', event.target.value)}
                placeholder="e.g. Madhapur"
              />
            </div>
            {fieldErrors.area ? (
              <div className="store-registration-inline-error" role="alert">
                <CircleAlert size={16} aria-hidden="true" />
                <span>{fieldErrors.area}</span>
              </div>
            ) : null}
          </div>

          <div className="store-registration-field">
            <label htmlFor="store-registration-state-readonly">
              STATE <span aria-hidden="true">*</span>
            </label>
            <div className="store-registration-input is-readonly">
              <Landmark size={18} aria-hidden="true" />
              <input
                id="store-registration-state-readonly"
                type="text"
                value={locationForm.state}
                readOnly
              />
            </div>
          </div>

          <div className="store-registration-field">
            <label htmlFor="store-registration-pincode">
              PINCODE <span aria-hidden="true">*</span>
            </label>
            <div className={`store-registration-input${fieldErrors.pincode || pincodeStatus.status === 'invalid' || pincodeStatus.status === 'mismatch' ? ' is-error' : ''}`}>
              <MapPinned size={18} aria-hidden="true" />
              <input
                id="store-registration-pincode"
                type="text"
                value={form.pincode}
                onChange={(event) => onFieldChange('pincode', event.target.value)}
                placeholder="e.g. 500092"
                inputMode="numeric"
                maxLength={6}
              />
            </div>
            <p className="store-registration-field-hint">The 6-digit postal code for your store location.</p>
            {fieldErrors.pincode ? (
              <div className="store-registration-inline-error" role="alert">
                <CircleAlert size={16} aria-hidden="true" />
                <span>{fieldErrors.pincode}</span>
              </div>
            ) : null}
            {pincodeStatus.status === 'loading' ? (
              <p className="store-registration-status-copy">Validating pincode...</p>
            ) : null}
            {pincodeStatus.status === 'match' ? (
              <p className="store-registration-status-copy is-success">
                Pincode verified against {pincodeStatus.apiDistrict}, {pincodeStatus.apiState}.
              </p>
            ) : null}
            {pincodeStatus.status === 'invalid' || pincodeStatus.status === 'mismatch' ? (
              <div className="store-registration-inline-error" role="alert">
                <CircleAlert size={16} aria-hidden="true" />
                <span>{pincodeStatus.message}</span>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="store-registration-section-block">
        <div className="store-registration-field">
          <label htmlFor="store-registration-address-line">Store address</label>
          <p className="store-registration-field-hint">Start typing and select from suggestions for accurate coordinates.</p>
        </div>
        <GoogleMapsProvider>
          <StoreLocationEditor
            pincode={form.pincode}
            address={form.addressLine}
            latitude={form.latitude}
            longitude={form.longitude}
            onChange={({ address, latitude, longitude }) => {
              onFieldChange('addressLine', address);
              onFieldChange('latitude', latitude);
              onFieldChange('longitude', longitude);
            }}
            onValidationChange={mapValidation.onValidationChange}
            onError={mapValidation.onError}
          />
        </GoogleMapsProvider>
        {fieldErrors.addressLine ? (
          <div className="store-registration-inline-error" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{fieldErrors.addressLine}</span>
          </div>
        ) : null}
        {fieldErrors.latitude || fieldErrors.longitude ? (
          <div className="store-registration-inline-error" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{fieldErrors.latitude || fieldErrors.longitude}</span>
          </div>
        ) : null}
        {mapValidation.error ? (
          <div className="store-registration-inline-error" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{mapValidation.error}</span>
          </div>
        ) : null}
      </section>

      <section className="store-registration-section-block">
        <div className="store-registration-section-heading">
          <h3>Shopping centre linkage</h3>
          <p>Skip this entire section if your store is standalone. Link it only when you operate inside a mall or shopping centre.</p>
        </div>
        <div className="store-registration-mode-toggle" role="radiogroup" aria-label="Shopping centre linkage">
          <button
            type="button"
            className={form.shoppingCentreMode === SHOPPING_CENTRE_MODE.STANDALONE ? 'is-selected' : ''}
            onClick={() => onFieldChange('shoppingCentreMode', SHOPPING_CENTRE_MODE.STANDALONE)}
          >
            Standalone store
          </button>
          <button
            type="button"
            className={form.shoppingCentreMode === SHOPPING_CENTRE_MODE.LINKED ? 'is-selected' : ''}
            onClick={() => onFieldChange('shoppingCentreMode', SHOPPING_CENTRE_MODE.LINKED)}
          >
            Link existing centre
          </button>
          <button
            type="button"
            className={form.shoppingCentreMode === SHOPPING_CENTRE_MODE.CUSTOM ? 'is-selected' : ''}
            onClick={() => onFieldChange('shoppingCentreMode', SHOPPING_CENTRE_MODE.CUSTOM)}
          >
            Add a new centre
          </button>
        </div>

        {usesShoppingCentre ? (
          <div className="store-registration-shopping-centre-panel">
            {form.shoppingCentreMode === SHOPPING_CENTRE_MODE.LINKED ? (
              <div className="store-registration-grid store-registration-grid--two">
                <div className="store-registration-field">
                  <label htmlFor="store-registration-shopping-centre">SHOPPING CENTRE</label>
                  <div className={`store-registration-input store-registration-input--select${fieldErrors.shoppingCentre || fieldErrors.shoppingCentreUuid ? ' is-error' : ''}`}>
                    <Landmark size={18} aria-hidden="true" />
                    <select
                      id="store-registration-shopping-centre"
                      value={form.shoppingCentreUuid}
                      onChange={(event) => {
                        const selectedMall = malls.find((mall) => String(mall.mallUuid) === event.target.value);
                        onFieldChange('shoppingCentreUuid', event.target.value);
                        onFieldChange('shoppingCentreName', selectedMall?.mallName || '');
                      }}
                    >
                      <option value="">{mallsLoading ? 'Loading shopping centres...' : 'Select a shopping centre'}</option>
                      {malls.map((mall) => (
                        <option key={mall.mallUuid} value={mall.mallUuid}>
                          {mall.mallName}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={18} aria-hidden="true" />
                  </div>
                  <p className="store-registration-field-hint">We fetch centres by your city so linkage stays tidy.</p>
                  {fieldErrors.shoppingCentre || fieldErrors.shoppingCentreUuid ? (
                    <div className="store-registration-inline-error" role="alert">
                      <CircleAlert size={16} aria-hidden="true" />
                      <span>{fieldErrors.shoppingCentre || fieldErrors.shoppingCentreUuid}</span>
                    </div>
                  ) : null}
                </div>

                <div className="store-registration-field">
                  <label htmlFor="store-registration-floor-or-unit">FLOOR / UNIT</label>
                  <div className={`store-registration-input${fieldErrors.floorOrUnit ? ' is-error' : ''}`}>
                    <MapPinned size={18} aria-hidden="true" />
                    <input
                      id="store-registration-floor-or-unit"
                      type="text"
                      value={form.floorOrUnit}
                      onChange={(event) => onFieldChange('floorOrUnit', event.target.value)}
                      placeholder="e.g. 1 or Unit 24B"
                    />
                  </div>
                  {fieldErrors.floorOrUnit ? (
                    <div className="store-registration-inline-error" role="alert">
                      <CircleAlert size={16} aria-hidden="true" />
                      <span>{fieldErrors.floorOrUnit}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {form.shoppingCentreMode === SHOPPING_CENTRE_MODE.CUSTOM ? (
              <div className="store-registration-section-stack">
                <div className="store-registration-grid store-registration-grid--two">
                  <div className="store-registration-field">
                    <label htmlFor="store-registration-custom-shopping-centre">SHOPPING CENTRE NAME</label>
                    <div className={`store-registration-input${fieldErrors.shoppingCentre || fieldErrors.shoppingCentreName ? ' is-error' : ''}`}>
                      <Landmark size={18} aria-hidden="true" />
                      <input
                        id="store-registration-custom-shopping-centre"
                        type="text"
                        value={customShoppingCentre.name}
                        onChange={(event) => {
                          onCustomShoppingCentreChange('name', event.target.value);
                          onFieldChange('shoppingCentreName', event.target.value);
                        }}
                        placeholder="e.g. Inorbit Mall"
                      />
                    </div>
                  </div>

                  <div className="store-registration-field">
                    <label htmlFor="store-registration-custom-floor-or-unit">FLOOR / UNIT</label>
                    <div className={`store-registration-input${fieldErrors.floorOrUnit ? ' is-error' : ''}`}>
                      <MapPinned size={18} aria-hidden="true" />
                      <input
                        id="store-registration-custom-floor-or-unit"
                        type="text"
                        value={form.floorOrUnit}
                        onChange={(event) => onFieldChange('floorOrUnit', event.target.value)}
                        placeholder="e.g. 1 or Kiosk A4"
                      />
                    </div>
                  </div>
                </div>
                <div className="store-registration-field">
                  <label htmlFor="store-registration-custom-shopping-centre-description">DESCRIPTION <span className="store-registration-optional">(optional)</span></label>
                  <div className="store-registration-input">
                    <Landmark size={18} aria-hidden="true" />
                    <input
                      id="store-registration-custom-shopping-centre-description"
                      type="text"
                      value={customShoppingCentre.description}
                      onChange={(event) => onCustomShoppingCentreChange('description', event.target.value)}
                      placeholder="Extra context for ops review"
                    />
                  </div>
                </div>
                {(fieldErrors.shoppingCentre || fieldErrors.shoppingCentreName || fieldErrors.floorOrUnit) ? (
                  <div className="store-registration-inline-error" role="alert">
                    <CircleAlert size={16} aria-hidden="true" />
                    <span>{fieldErrors.shoppingCentre || fieldErrors.shoppingCentreName || fieldErrors.floorOrUnit}</span>
                  </div>
                ) : null}
                <div className="store-registration-shopping-centre-actions">
                  <button
                    type="button"
                    className="store-registration-button store-registration-button--ghost"
                    onClick={onRegisterCustomShoppingCentre}
                    disabled={shoppingCentreSaving}
                  >
                    {shoppingCentreSaving ? 'Saving shopping centre...' : 'Save shopping centre'}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
};

const StoreCodeAndReviewStepPanel = ({
  draftData,
  locationForm,
  detailsForm,
  addressForm,
  reviewForm,
  fieldErrors,
  onGenerateStoreCode,
  onReviewToggle,
  onEditStep,
  generatingCode
}) => {
  const reservedStoreCode = draftData?.reservedStoreCode || '';
  const shoppingCentreLabel = addressForm.shoppingCentreMode === SHOPPING_CENTRE_MODE.STANDALONE
    ? 'Standalone'
    : addressForm.shoppingCentreName || '—';

  return (
    <div className="store-registration-section-stack">
      <section className="store-registration-section-block">
        <div className="store-registration-section-heading">
          <h3>Store code</h3>
          <p>Generate a secure, unique identifier for this store.</p>
        </div>
        <div className="store-registration-store-code-row">
          <div className={`store-registration-input store-registration-input--code${fieldErrors.reservedStoreCode ? ' is-error' : ''}`}>
            <Sparkles size={18} aria-hidden="true" />
            <input
              type="text"
              readOnly
              value={reservedStoreCode || 'Click generate to create a code'}
            />
          </div>
          <button
            type="button"
            className="store-registration-button store-registration-button--secondary"
            onClick={onGenerateStoreCode}
            disabled={generatingCode}
          >
            <Sparkles size={18} aria-hidden="true" />
            <span>{generatingCode ? 'Generating...' : 'Generate'}</span>
          </button>
        </div>
        {fieldErrors.reservedStoreCode ? (
          <div className="store-registration-inline-error" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{fieldErrors.reservedStoreCode}</span>
          </div>
        ) : null}
        <div className="store-registration-warning-card">
          <ShieldAlert size={20} aria-hidden="true" />
          <div>
            <strong>Store code is critical</strong>
            <p>This code uniquely identifies your store. Save it somewhere safe because your team will use it for sign-in.</p>
          </div>
        </div>
      </section>

      <section className="store-registration-section-block">
        <div className="store-registration-section-heading">
          <h3>Review your details</h3>
          <p>Make sure everything is correct before creating your store.</p>
        </div>

        <div className="store-registration-review-stack">
          <ReviewSection title="Business" onEdit={() => onEditStep('store-details')}>
            <ReviewRow label="Business name" value={detailsForm.storeName} />
            <ReviewRow label="Category" value={detailsForm.businessCategory} />
            <ReviewRow label="Email" value={detailsForm.email} />
            <ReviewRow label="Phone" value={detailsForm.phone ? `Mobile · +91 ${detailsForm.phone}` : '—'} />
            <ReviewRow label="Taxes" value={detailsForm.chargesTaxSeparately ? 'Charged separately at store' : 'Included in price'} />
          </ReviewSection>

          <ReviewSection title="Location" onEdit={() => onEditStep('address-and-venue')}>
            <ReviewRow label="Address" value={addressForm.addressLine} />
            <ReviewRow label="Locality" value={[addressForm.area, addressForm.city, locationForm.state].filter(Boolean).join(', ')} />
            <ReviewRow label="Pincode" value={addressForm.pincode} />
            <ReviewRow label="Shopping centre" value={shoppingCentreLabel} />
            <ReviewRow label="Floor / unit" value={addressForm.floorOrUnit} />
            <ReviewRow label="Map marker" value={addressForm.latitude && addressForm.longitude ? 'Placed ✓' : 'Not placed'} />
          </ReviewSection>

          <ReviewSection title="Region" onEdit={() => onEditStep('location')}>
            <ReviewRow label="State" value={locationForm.state} />
            <ReviewRow label="Marketplace" value={MARKETPLACE_OPTIONS.find((option) => option.value === locationForm.marketplaceKey)?.label || 'Zopeo Marketplace'} />
          </ReviewSection>
        </div>

        <label className={`store-registration-consent${fieldErrors.reviewConfirmed || fieldErrors.termsAccepted || fieldErrors.privacyAccepted ? ' is-error' : ''}`}>
          <input
            type="checkbox"
            checked={reviewForm.confirmed}
            onChange={(event) => onReviewToggle(event.target.checked)}
          />
          <span>
            I confirm the information above is accurate and agree to Zopeo’s <a href="/support" target="_blank" rel="noreferrer">Merchant Terms</a> and <a href="/support" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </span>
        </label>
        {(fieldErrors.reviewConfirmed || fieldErrors.termsAccepted || fieldErrors.privacyAccepted) ? (
          <div className="store-registration-inline-error" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{fieldErrors.reviewConfirmed || fieldErrors.termsAccepted || fieldErrors.privacyAccepted}</span>
          </div>
        ) : null}
      </section>
    </div>
  );
};

const CompletionPanel = ({
  summary,
  storeCode,
  onCopyCode,
  onRegisterAnother,
  onGoToLogin
}) => (
  <div className="store-registration-complete-panel">
    <span className="store-registration-complete-kicker">Store created</span>
    <div className="store-registration-complete-icon">
      <Check size={48} aria-hidden="true" />
    </div>
    <div className="store-registration-complete-copy">
      <h2>{summary.storeName} is ready for approval</h2>
      <p>
        Your storefront registration has been submitted successfully. Save the store code below. Your team will use it to sign in after approval.
      </p>
    </div>
    <div className="store-registration-complete-code">
      <span>{storeCode || 'Store code pending'}</span>
      <button type="button" onClick={onCopyCode} aria-label="Copy store code">
        <Copy size={18} aria-hidden="true" />
      </button>
    </div>
    <div className="store-registration-complete-actions">
      <button type="button" className="store-registration-button store-registration-button--ghost" onClick={onRegisterAnother}>
        Register another
      </button>
      <button type="button" className="store-registration-button store-registration-button--primary" onClick={onGoToLogin}>
        <span>Go to login</span>
        <ArrowRight size={18} aria-hidden="true" />
      </button>
    </div>
  </div>
);

const StoreRegistrationOnboardingPage = ({
  route,
  onNavigate,
  request,
  onToast
}) => {
  const { draftUuid, stepId } = getStepFromRoute(route);
  const stepDefinition = STEP_DEFINITIONS.find((step) => step.id === stepId) || STEP_DEFINITIONS[0];
  const currentStepIndex = stepIndexLookup[stepId] ?? 0;

  const [draftData, setDraftData] = useState(null);
  const [locationForm, setLocationForm] = useState(DEFAULT_LOCATION);
  const [detailsForm, setDetailsForm] = useState(DEFAULT_DETAILS);
  const [addressForm, setAddressForm] = useState(DEFAULT_ADDRESS);
  const [reviewForm, setReviewForm] = useState(DEFAULT_REVIEW);
  const [customShoppingCentre, setCustomShoppingCentre] = useState(DEFAULT_CUSTOM_SHOPPING_CENTRE);
  const [passwordModal, setPasswordModal] = useState(DEFAULT_PASSWORD_MODAL);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState('idle');
  const [saveMessage, setSaveMessage] = useState('Your selections are saved automatically as you go.');
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [bootstrappingDraft, setBootstrappingDraft] = useState(false);
  const [malls, setMalls] = useState([]);
  const [mallsLoading, setMallsLoading] = useState(false);
  const [shoppingCentreSaving, setShoppingCentreSaving] = useState(false);
  const [pincodeStatus, setPincodeStatus] = useState({ status: 'idle', apiState: '', apiDistrict: '', message: '' });
  const [mapValidation, setMapValidation] = useState({ isValid: true, error: '' });
  const [generatingCode, setGeneratingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const lastSavedPayloadsRef = useRef({
    location: '',
    'store-details': '',
    'address-and-venue': ''
  });
  const locationAutosaveTimerRef = useRef(null);
  const storeDetailsAutosaveTimerRef = useRef(null);
  const addressAutosaveTimerRef = useRef(null);
  const skipAutosaveRef = useRef(false);
  const latestStepRef = useRef(stepId);

  latestStepRef.current = stepId;

  const summary = buildSummary(draftData, locationForm, detailsForm, addressForm);

  const applyDraftResponse = (response) => {
    setDraftData(response);
    skipAutosaveRef.current = true;

    setLocationForm({
      state: response?.state || DEFAULT_LOCATION.state,
      marketplaceKey: response?.marketplaceKey || DEFAULT_LOCATION.marketplaceKey
    });

    setDetailsForm({
      storeName: response?.storeName || DEFAULT_DETAILS.storeName,
      businessCategory: response?.businessCategory || DEFAULT_DETAILS.businessCategory,
      clientType: response?.clientType || DEFAULT_DETAILS.clientType,
      email: response?.email || DEFAULT_DETAILS.email,
      phone: normalizeDigits(response?.phone || DEFAULT_DETAILS.phone),
      phoneType: response?.phoneType || DEFAULT_DETAILS.phoneType,
      chargesTaxSeparately: Boolean(response?.chargesTaxSeparately)
    });

    setAddressForm({
      city: response?.city || DEFAULT_ADDRESS.city,
      area: response?.area || DEFAULT_ADDRESS.area,
      pincode: response?.pincode || DEFAULT_ADDRESS.pincode,
      addressLine: response?.addressLine || DEFAULT_ADDRESS.addressLine,
      latitude: response?.latitude ?? DEFAULT_ADDRESS.latitude,
      longitude: response?.longitude ?? DEFAULT_ADDRESS.longitude,
      shoppingCentreMode: response?.shoppingCentreMode || DEFAULT_ADDRESS.shoppingCentreMode,
      shoppingCentreUuid: response?.shoppingCentreUuid || DEFAULT_ADDRESS.shoppingCentreUuid,
      shoppingCentreName: response?.shoppingCentreName || DEFAULT_ADDRESS.shoppingCentreName,
      floorOrUnit: response?.floorOrUnit || DEFAULT_ADDRESS.floorOrUnit
    });

    setReviewForm({
      confirmed: Boolean(response?.reviewConfirmed)
    });

    setCustomShoppingCentre((current) => ({
      ...current,
      name: response?.shoppingCentreName || current.name || '',
      description: current.description || ''
    }));

    setMapValidation({
      isValid: Boolean(response?.latitude && response?.longitude),
      error: ''
    });

    lastSavedPayloadsRef.current.location = serializePayload({
      state: response?.state || '',
      marketplaceKey: response?.marketplaceKey || DEFAULT_LOCATION.marketplaceKey
    });
    lastSavedPayloadsRef.current['store-details'] = serializePayload({
      storeName: response?.storeName || '',
      businessCategory: response?.businessCategory || '',
      clientType: response?.clientType || '',
      email: response?.email || '',
      phone: normalizeDigits(response?.phone || ''),
      phoneType: response?.phoneType || DEFAULT_DETAILS.phoneType,
      chargesTaxSeparately: Boolean(response?.chargesTaxSeparately)
    });
    lastSavedPayloadsRef.current['address-and-venue'] = serializePayload({
      city: response?.city || '',
      area: response?.area || '',
      pincode: response?.pincode || '',
      addressLine: response?.addressLine || '',
      latitude: response?.latitude ?? null,
      longitude: response?.longitude ?? null,
      shoppingCentreMode: response?.shoppingCentreMode || DEFAULT_ADDRESS.shoppingCentreMode,
      shoppingCentreUuid: response?.shoppingCentreUuid || '',
      shoppingCentreName: response?.shoppingCentreName || '',
      floorOrUnit: response?.floorOrUnit || ''
    });

    setTimeout(() => {
      skipAutosaveRef.current = false;
    }, 0);
  };

  const setFooterSaving = (message = 'Saving progress...') => {
    setSaveStatus('saving');
    setSaveMessage(message);
  };

  const setFooterSaved = (message = 'Progress saved') => {
    setSaveStatus('saved');
    setSaveMessage(message);
  };

  const setFooterError = (message = 'Unable to save right now. Please try again.') => {
    setSaveStatus('error');
    setSaveMessage(message);
  };

  const updateLocationField = (field, value) => {
    setLocationForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: '' }));
  };

  const updateDetailsField = (field, value) => {
    const nextValue = field === 'phone' ? normalizeDigits(value).slice(0, 15) : value;
    setDetailsForm((current) => ({ ...current, [field]: nextValue }));
    setFieldErrors((current) => ({ ...current, [field]: '' }));
  };

  const updateAddressField = (field, value) => {
    setAddressForm((current) => {
      const next = { ...current, [field]: field === 'pincode' ? normalizeDigits(value).slice(0, 6) : value };

      if (field === 'shoppingCentreMode' && value === SHOPPING_CENTRE_MODE.STANDALONE) {
        next.shoppingCentreUuid = '';
        next.shoppingCentreName = '';
        next.floorOrUnit = '';
      }

      if (field === 'shoppingCentreMode' && value === SHOPPING_CENTRE_MODE.LINKED) {
        next.shoppingCentreName = '';
      }

      if (field === 'shoppingCentreMode' && value === SHOPPING_CENTRE_MODE.CUSTOM) {
        next.shoppingCentreUuid = '';
      }

      return next;
    });

    if (field === 'pincode') {
      setMapValidation((current) => ({ ...current, error: '' }));
    }

    if (field === 'shoppingCentreMode') {
      setFieldErrors((current) => ({
        ...current,
        shoppingCentre: '',
        shoppingCentreUuid: '',
        shoppingCentreName: '',
        floorOrUnit: ''
      }));
      return;
    }

    setFieldErrors((current) => ({ ...current, [field]: '' }));
  };

  const updateCustomShoppingCentreField = (field, value) => {
    setCustomShoppingCentre((current) => ({ ...current, [field]: value }));
  };

  const updatePasswordModalField = (field, value) => {
    setPasswordModal((current) => ({ ...current, [field]: value }));
    setPasswordError('');
  };

  const selectCategory = (option) => {
    setDetailsForm((current) => ({
      ...current,
      businessCategory: option.value,
      clientType: option.clientType
    }));
    setFieldErrors((current) => ({
      ...current,
      businessCategory: '',
      clientType: ''
    }));
  };

  const persistLocation = async ({ silent = false } = {}) => {
    if (!draftUuid || !normalizeText(locationForm.state)) {
      return false;
    }

    const payload = {
      state: normalizeText(locationForm.state),
      marketplaceKey: normalizeText(locationForm.marketplaceKey) || MARKETPLACE_OPTIONS[0].value
    };
    const serialized = serializePayload(payload);
    if (silent && serialized === lastSavedPayloadsRef.current.location) {
      return true;
    }

    if (!silent) {
      setFooterSaving();
    }

    try {
      const response = unwrapApiData(await request(AUTH_STORE_REGISTRATION_DRAFT_LOCATION_STEP(draftUuid), {
        method: 'PATCH',
        body: JSON.stringify(payload),
        requestName: 'store-registration-location-step'
      }));
      setFieldErrors((current) => ({ ...current, state: '' }));
      lastSavedPayloadsRef.current.location = serialized;
      applyDraftResponse(response);
      setFooterSaved('Progress saved');
      return true;
    } catch (error) {
      const errors = extractFieldErrors(error);
      if (Object.keys(errors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...errors }));
      }
      setFooterError(error?.data?.message || 'Unable to save location right now.');
      return false;
    }
  };

  const persistStoreDetails = async ({ silent = false } = {}) => {
    if (!draftUuid) {
      return false;
    }

    const payload = {
      storeName: normalizeText(detailsForm.storeName),
      businessCategory: normalizeText(detailsForm.businessCategory),
      clientType: normalizeText(detailsForm.clientType) || (CATEGORY_OPTIONS.find(
        (option) => option.value === detailsForm.businessCategory
      )?.clientType || 'RETAIL'),
      email: normalizeText(detailsForm.email),
      phone: normalizeDigits(detailsForm.phone),
      phoneType: detailsForm.phoneType || 'mobile',
      chargesTaxSeparately: Boolean(detailsForm.chargesTaxSeparately)
    };
    const serialized = serializePayload(payload);
    if (silent && serialized === lastSavedPayloadsRef.current['store-details']) {
      return true;
    }

    if (!silent) {
      setFooterSaving();
    }

    try {
      const response = unwrapApiData(await request(AUTH_STORE_REGISTRATION_DRAFT_STORE_DETAILS_STEP(draftUuid), {
        method: 'PATCH',
        body: JSON.stringify(payload),
        requestName: 'store-registration-store-details-step'
      }));
      setFieldErrors((current) => ({
        ...current,
        storeName: '',
        businessCategory: '',
        email: '',
        phone: ''
      }));
      lastSavedPayloadsRef.current['store-details'] = serialized;
      applyDraftResponse(response);
      setFooterSaved('Progress saved');
      return true;
    } catch (error) {
      const errors = extractFieldErrors(error);
      if (Object.keys(errors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...errors }));
      }
      setFooterError(error?.data?.message || 'Unable to save store details right now.');
      return false;
    }
  };

  const persistAddressAndVenue = async ({ silent = false } = {}) => {
    if (!draftUuid) {
      return false;
    }

    const payload = {
      city: normalizeText(addressForm.city),
      area: normalizeText(addressForm.area),
      pincode: normalizeDigits(addressForm.pincode),
      addressLine: normalizeText(addressForm.addressLine),
      latitude: addressForm.latitude,
      longitude: addressForm.longitude,
      shoppingCentreMode: addressForm.shoppingCentreMode,
      shoppingCentreUuid: normalizeText(addressForm.shoppingCentreUuid),
      shoppingCentreName: normalizeText(addressForm.shoppingCentreName),
      floorOrUnit: normalizeText(addressForm.floorOrUnit)
    };
    const serialized = serializePayload(payload);
    if (silent && serialized === lastSavedPayloadsRef.current['address-and-venue']) {
      return true;
    }

    if (!silent) {
      setFooterSaving();
    }

    try {
      const response = unwrapApiData(await request(AUTH_STORE_REGISTRATION_DRAFT_ADDRESS_AND_VENUE_STEP(draftUuid), {
        method: 'PATCH',
        body: JSON.stringify(payload),
        requestName: 'store-registration-address-and-venue-step'
      }));
      setFieldErrors((current) => ({
        ...current,
        city: '',
        area: '',
        pincode: '',
        addressLine: '',
        latitude: '',
        longitude: '',
        shoppingCentre: '',
        shoppingCentreUuid: '',
        shoppingCentreName: '',
        floorOrUnit: ''
      }));
      lastSavedPayloadsRef.current['address-and-venue'] = serialized;
      applyDraftResponse(response);
      setFooterSaved('Progress saved');
      return true;
    } catch (error) {
      const errors = extractFieldErrors(error);
      if (Object.keys(errors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...errors }));
      }
      setFooterError(error?.data?.message || 'Unable to save address and venue right now.');
      return false;
    }
  };

  const persistReview = async () => {
    if (!draftUuid) {
      return false;
    }

    setFooterSaving();

    try {
      const response = unwrapApiData(await request(AUTH_STORE_REGISTRATION_DRAFT_STORE_CODE_REVIEW_STEP(draftUuid), {
        method: 'PATCH',
        body: JSON.stringify({
          reviewConfirmed: reviewForm.confirmed,
          termsAccepted: reviewForm.confirmed,
          privacyAccepted: reviewForm.confirmed
        }),
        requestName: 'store-registration-store-code-review-step'
      }));
      setFieldErrors((current) => ({
        ...current,
        reviewConfirmed: '',
        termsAccepted: '',
        privacyAccepted: '',
        reservedStoreCode: ''
      }));
      applyDraftResponse(response);
      setFooterSaved('Progress saved');
      return true;
    } catch (error) {
      const errors = extractFieldErrors(error);
      if (Object.keys(errors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...errors }));
      }
      setFooterError(error?.data?.message || 'Unable to save review right now.');
      return false;
    }
  };

  const validateLocationLocal = () => {
    const errors = {};
    if (!normalizeText(locationForm.state)) {
      errors.state = 'State is required';
    }
    return errors;
  };

  const validateStoreDetailsLocal = () => {
    const errors = {};
    if (!normalizeText(detailsForm.storeName)) {
      errors.storeName = 'Business name is required';
    }
    if (!normalizeText(detailsForm.businessCategory)) {
      errors.businessCategory = 'Business category is required';
    }
    if (!isEmailValid(detailsForm.email)) {
      errors.email = 'Enter a valid email address';
    }
    if (!isPhoneValid(detailsForm.phone)) {
      errors.phone = 'Enter a valid phone number';
    }
    return errors;
  };

  const validateAddressLocal = () => {
    const errors = {};
    if (!normalizeText(addressForm.city)) {
      errors.city = 'City is required';
    }
    if (!normalizeText(addressForm.area)) {
      errors.area = 'Area or locality is required';
    }
    if (!isPincodeValidFormat(addressForm.pincode)) {
      errors.pincode = 'Enter a valid 6-digit pincode';
    }
    if (!normalizeText(addressForm.addressLine)) {
      errors.addressLine = 'Store address is required';
    }
    if (addressForm.latitude == null || addressForm.longitude == null) {
      errors.latitude = 'Place your store marker on the map';
    }
    if (pincodeStatus.status === 'invalid' || pincodeStatus.status === 'mismatch') {
      errors.pincode = pincodeStatus.message || 'Pincode validation failed';
    }
    if (mapValidation.error) {
      errors.addressLine = mapValidation.error;
    }
    if (addressForm.shoppingCentreMode !== SHOPPING_CENTRE_MODE.STANDALONE) {
      if (!normalizeText(addressForm.shoppingCentreUuid) && !normalizeText(addressForm.shoppingCentreName)) {
        errors.shoppingCentre = 'Select or enter a shopping centre';
      }
      if (!normalizeText(addressForm.floorOrUnit)) {
        errors.floorOrUnit = 'Floor or unit is required when shopping centre linkage is used';
      }
    }
    return errors;
  };

  const handleGenerateStoreCode = async () => {
    if (!draftUuid) {
      return;
    }

    const detailErrors = validateStoreDetailsLocal();
    const addressErrors = validateAddressLocal();
    if (Object.keys(detailErrors).length > 0 || Object.keys(addressErrors).length > 0) {
      setFieldErrors((current) => ({ ...current, ...detailErrors, ...addressErrors }));
      onToast?.({
        message: 'Finish the earlier steps before generating a store code.',
        variant: 'error'
      });
      return;
    }

    setGeneratingCode(true);
    setFooterSaving('Generating store code...');

    try {
      const response = unwrapApiData(await request(AUTH_STORE_REGISTRATION_DRAFT_STORE_CODE_GENERATE(draftUuid), {
        method: 'POST',
        requestName: 'store-registration-generate-store-code'
      }));
      applyDraftResponse(response);
      setFieldErrors((current) => ({ ...current, reservedStoreCode: '' }));
      setFooterSaved('Store code generated');
    } catch (error) {
      const errors = extractFieldErrors(error);
      if (Object.keys(errors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...errors }));
      }
      setFooterError(error?.data?.message || 'Unable to generate a store code right now.');
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleRegisterCustomShoppingCentre = async () => {
    if (!request) {
      return;
    }

    const mallName = normalizeText(customShoppingCentre.name);
    if (!mallName || !normalizeText(addressForm.city) || !normalizeText(locationForm.state) || !isPincodeValidFormat(addressForm.pincode)) {
      setFieldErrors((current) => ({
        ...current,
        shoppingCentreName: !mallName ? 'Shopping centre name is required' : current.shoppingCentreName,
        pincode: !isPincodeValidFormat(addressForm.pincode) ? 'Enter a valid 6-digit pincode before registering a shopping centre' : current.pincode
      }));
      return;
    }

    setShoppingCentreSaving(true);
    try {
      const response = unwrapApiData(await request(AUTH_MALLS_REGISTER, {
        method: 'POST',
        body: JSON.stringify({
          mallName,
          description: normalizeText(customShoppingCentre.description),
          city: normalizeText(addressForm.city),
          state: normalizeText(locationForm.state),
          pincode: normalizeDigits(addressForm.pincode),
          area: normalizeText(addressForm.area),
          street: normalizeText(addressForm.addressLine)
        }),
        requestName: 'store-registration-register-shopping-centre'
      }));

      const mallUuid = response?.mallUuid || response?.data?.mallUuid || '';
      if (mallUuid) {
        setAddressForm((current) => ({
          ...current,
          shoppingCentreUuid: mallUuid,
          shoppingCentreName: mallName
        }));
        setMalls((current) => [{
          mallUuid,
          mallName
        }, ...current.filter((mall) => String(mall.mallUuid) !== String(mallUuid))]);
      }
      setFieldErrors((current) => ({
        ...current,
        shoppingCentre: '',
        shoppingCentreName: ''
      }));
      onToast?.({
        message: 'Shopping centre saved and linked to this draft.',
        variant: 'success'
      });
    } catch (error) {
      onToast?.({
        message: error?.data?.message || error?.message || 'Unable to save shopping centre right now.',
        variant: 'error'
      });
    } finally {
      setShoppingCentreSaving(false);
    }
  };

  const handleSubmitDraft = async () => {
    if (!draftUuid) {
      return;
    }

    const validationError = validatePassword(passwordModal.password, passwordModal.confirmPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    setSubmitting(true);
    setPasswordError('');
    setFooterSaving('Creating store...');

    try {
      const response = unwrapApiData(await request(AUTH_STORE_REGISTRATION_DRAFT_SUBMIT(draftUuid), {
        method: 'POST',
        body: JSON.stringify({
          password: passwordModal.password
        }),
        requestName: 'store-registration-submit-draft'
      }));
      applyDraftResponse(response);
      setPasswordModal(DEFAULT_PASSWORD_MODAL);
      setFooterSaved('Registration submitted');
      onToast?.({
        message: "You'll receive an email on your registration process. Please wait for some time.",
        variant: 'success'
      });
      onNavigate('/');
    } catch (error) {
      const errors = extractFieldErrors(error);
      if (Object.keys(errors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...errors }));
      }
      setPasswordError(error?.data?.message || error?.message || 'Unable to submit this draft right now.');
      setFooterError(error?.data?.message || 'Unable to submit this draft right now.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = async () => {
    if (!draftUuid) {
      return;
    }

    if (stepId === 'location') {
      const errors = validateLocationLocal();
      if (Object.keys(errors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...errors }));
        return;
      }

      const saved = await persistLocation();
      if (saved) {
        onNavigate(getStepHref('store-details', draftUuid));
      }
      return;
    }

    if (stepId === 'store-details') {
      const errors = validateStoreDetailsLocal();
      if (Object.keys(errors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...errors }));
        return;
      }

      const saved = await persistStoreDetails();
      if (saved) {
        onNavigate(getStepHref('address-and-venue', draftUuid));
      }
      return;
    }

    if (stepId === 'address-and-venue') {
      const errors = validateAddressLocal();
      if (Object.keys(errors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...errors }));
        return;
      }

      const saved = await persistAddressAndVenue();
      if (saved) {
        onNavigate(getStepHref('store-code-review', draftUuid));
      }
      return;
    }

    if (stepId === 'store-code-review') {
      const saved = await persistReview();
      if (saved) {
        setPasswordModal((current) => ({ ...current, open: true }));
      }
    }
  };

  const handleBack = () => {
    if (!draftUuid) {
      onNavigate('/login');
      return;
    }

    if (stepId === 'store-details') {
      onNavigate(getStepHref('location', draftUuid));
      return;
    }
    if (stepId === 'address-and-venue') {
      onNavigate(getStepHref('store-details', draftUuid));
      return;
    }
    if (stepId === 'store-code-review') {
      onNavigate(getStepHref('address-and-venue', draftUuid));
      return;
    }
    if (stepId === 'complete') {
      onNavigate('/login');
      return;
    }

    onNavigate('/login');
  };

  const handleStepNavigation = (targetStepId) => {
    if (!draftUuid || targetStepId === stepId || targetStepId === 'complete') {
      return;
    }

    const targetIndex = stepIndexLookup[targetStepId] ?? 0;
    if (targetIndex > currentStepIndex + 1 && !draftData?.summary?.storeName) {
      return;
    }

    onNavigate(getStepHref(targetStepId, draftUuid));
  };

  const copyStoreCode = async () => {
    const storeCode = draftData?.reservedStoreCode || summary.storeCode;
    if (!storeCode || storeCode === '—') {
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(storeCode);
      onToast?.({
        message: 'Store code copied',
        variant: 'success'
      });
      return;
    }

    onToast?.({
      message: 'Clipboard is unavailable in this browser.',
      variant: 'error'
    });
  };

  useEffect(() => {
    if (route !== '/register' || !request) {
      return undefined;
    }

    let cancelled = false;
    setBootstrappingDraft(true);

    request(AUTH_STORE_REGISTRATION_DRAFTS, {
      method: 'POST',
      body: JSON.stringify({}),
      requestName: 'store-registration-create-draft'
    })
      .then((response) => {
        if (cancelled) {
          return;
        }
        const payload = unwrapApiData(response);
        if (!payload?.draftUuid) {
          throw new Error('Draft UUID was not returned');
        }
        onNavigate(getStepHref('location', payload.draftUuid));
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        onToast?.({
          message: error?.data?.message || error?.message || 'Unable to start registration right now.',
          variant: 'error'
        });
      })
      .finally(() => {
        if (!cancelled) {
          setBootstrappingDraft(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [onNavigate, onToast, request, route]);

  useEffect(() => {
    if (!draftUuid || !request) {
      return undefined;
    }

    let cancelled = false;
    setLoadingDraft(true);

    request(AUTH_STORE_REGISTRATION_DRAFT(draftUuid), {
      method: 'GET',
      requestName: 'store-registration-get-draft'
    })
      .then((response) => {
        if (cancelled) {
          return;
        }
        applyDraftResponse(unwrapApiData(response));
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        onToast?.({
          message: error?.data?.message || error?.message || 'Unable to load this registration draft.',
          variant: 'error'
        });
        onNavigate('/register');
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingDraft(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [draftUuid, onNavigate, onToast, request]);

  useEffect(() => {
    if (skipAutosaveRef.current || !draftUuid || stepId !== 'location') {
      return undefined;
    }

    const payload = {
      state: normalizeText(locationForm.state),
      marketplaceKey: normalizeText(locationForm.marketplaceKey) || MARKETPLACE_OPTIONS[0].value
    };
    if (!payload.state) {
      return undefined;
    }

    clearTimeout(locationAutosaveTimerRef.current);
    locationAutosaveTimerRef.current = setTimeout(() => {
      persistLocation({ silent: true });
    }, 500);

    return () => clearTimeout(locationAutosaveTimerRef.current);
  }, [draftUuid, locationForm.marketplaceKey, locationForm.state, stepId]);

  useEffect(() => {
    if (skipAutosaveRef.current || !draftUuid || stepId !== 'store-details') {
      return undefined;
    }

    const payload = {
      storeName: normalizeText(detailsForm.storeName),
      businessCategory: normalizeText(detailsForm.businessCategory),
      email: normalizeText(detailsForm.email),
      phone: normalizeDigits(detailsForm.phone)
    };
    if (!payload.storeName || !payload.businessCategory || !payload.email || !payload.phone) {
      return undefined;
    }

    clearTimeout(storeDetailsAutosaveTimerRef.current);
    storeDetailsAutosaveTimerRef.current = setTimeout(() => {
      persistStoreDetails({ silent: true });
    }, 700);

    return () => clearTimeout(storeDetailsAutosaveTimerRef.current);
  }, [detailsForm.businessCategory, detailsForm.clientType, detailsForm.email, detailsForm.phone, detailsForm.phoneType, detailsForm.storeName, draftUuid, stepId]);

  useEffect(() => {
    if (skipAutosaveRef.current || !draftUuid || stepId !== 'address-and-venue') {
      return undefined;
    }

    const payload = {
      city: normalizeText(addressForm.city),
      area: normalizeText(addressForm.area),
      pincode: normalizeDigits(addressForm.pincode),
      addressLine: normalizeText(addressForm.addressLine),
      latitude: addressForm.latitude,
      longitude: addressForm.longitude
    };
    if (!payload.city || !payload.area || !payload.pincode || !payload.addressLine || payload.latitude == null || payload.longitude == null) {
      return undefined;
    }

    clearTimeout(addressAutosaveTimerRef.current);
    addressAutosaveTimerRef.current = setTimeout(() => {
      persistAddressAndVenue({ silent: true });
    }, 700);

    return () => clearTimeout(addressAutosaveTimerRef.current);
  }, [
    addressForm.addressLine,
    addressForm.area,
    addressForm.city,
    addressForm.floorOrUnit,
    addressForm.latitude,
    addressForm.longitude,
    addressForm.pincode,
    addressForm.shoppingCentreMode,
    addressForm.shoppingCentreName,
    addressForm.shoppingCentreUuid,
    draftUuid,
    stepId
  ]);

  useEffect(() => {
    if (!draftUuid || stepId !== 'address-and-venue') {
      return undefined;
    }

    if (addressForm.shoppingCentreMode !== SHOPPING_CENTRE_MODE.LINKED || !normalizeText(addressForm.city)) {
      setMalls([]);
      return undefined;
    }

    let cancelled = false;
    setMallsLoading(true);

    request(`${AUTH_MALLS}?city=${encodeURIComponent(normalizeText(addressForm.city))}`, {
      method: 'GET',
      requestName: 'store-registration-shopping-centres'
    })
      .then((response) => {
        if (cancelled) {
          return;
        }
        const payload = unwrapApiData(response);
        setMalls(Array.isArray(payload) ? payload : []);
      })
      .catch(() => {
        if (!cancelled) {
          setMalls([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMallsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [addressForm.city, addressForm.shoppingCentreMode, draftUuid, request, stepId]);

  useEffect(() => {
    if (stepId !== 'address-and-venue') {
      return undefined;
    }

    const pincode = normalizeDigits(addressForm.pincode);
    const city = normalizeText(addressForm.city);
    const state = normalizeText(locationForm.state);

    if (!pincode) {
      setPincodeStatus({ status: 'idle', apiState: '', apiDistrict: '', message: '' });
      return undefined;
    }

    if (pincode.length > 0 && pincode.length < 6) {
      setPincodeStatus({
        status: 'invalid',
        apiState: '',
        apiDistrict: '',
        message: 'Enter a valid 6-digit pincode.'
      });
      return undefined;
    }

    if (!isPincodeValidFormat(pincode) || !state) {
      return undefined;
    }

    let cancelled = false;
    setPincodeStatus({ status: 'loading', apiState: '', apiDistrict: '', message: '' });

    const timer = setTimeout(() => {
      request(AUTH_PINCODE_LOOKUP(pincode), {
        method: 'GET',
        requestName: 'store-registration-pincode-lookup'
      })
        .then((response) => {
          if (cancelled) {
            return;
          }
          const payload = unwrapApiData(response);
          const apiState = normalizeText(payload?.state);
          const apiDistrict = normalizeText(payload?.district);
          if (!apiState || !apiDistrict) {
            setPincodeStatus({
              status: 'invalid',
              apiState: '',
              apiDistrict: '',
              message: 'Unable to verify this pincode.'
            });
            return;
          }

          const normalizeForCompare = (value) => String(value || '').toLowerCase().replace(/[^a-z\s]/g, '').trim();
          const stateNorm = normalizeForCompare(state);
          const apiStateNorm = normalizeForCompare(apiState);
          const apiDistrictNorm = normalizeForCompare(apiDistrict);

          const stateMatch = stateNorm && apiStateNorm
            && (stateNorm.includes(apiStateNorm) || apiStateNorm.includes(stateNorm));

          if (stateMatch) {
            setPincodeStatus({
              status: 'match',
              apiState,
              apiDistrict,
              message: ''
            });
            if (!normalizeText(addressForm.area)) {
              setAddressForm((current) => ({
                ...current,
                area: current.area || apiDistrict
              }));
            }
            return;
          }

          setPincodeStatus({
            status: 'mismatch',
            apiState,
            apiDistrict,
            message: `This pincode maps to ${apiDistrict}, ${apiState}. Update the state or enter the correct store pincode.`
          });
        })
        .catch(() => {
          if (!cancelled) {
            setPincodeStatus({
              status: 'idle',
              apiState: '',
              apiDistrict: '',
              message: ''
            });
          }
        });
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [addressForm.area, addressForm.city, addressForm.pincode, locationForm.state, request, stepId]);

  const stepItems = STEP_DEFINITIONS.filter((step) => step.id !== 'complete');

  const renderContent = () => {
    if ((bootstrappingDraft || loadingDraft) && !draftData) {
      return (
        <div className="store-registration-loading-panel" aria-live="polite">
          <div className="store-registration-loading-spinner" aria-hidden="true" />
          <p>{bootstrappingDraft ? 'Starting your registration...' : 'Loading your saved registration...'}</p>
        </div>
      );
    }

    if (stepId === 'location') {
      return (
        <LocationStepPanel
          form={locationForm}
          fieldErrors={fieldErrors}
          onChange={updateLocationField}
        />
      );
    }

    if (stepId === 'store-details') {
      return (
        <StoreDetailsStepPanel
          form={detailsForm}
          fieldErrors={fieldErrors}
          onFieldChange={updateDetailsField}
          onCategorySelect={selectCategory}
        />
      );
    }

    if (stepId === 'address-and-venue') {
      return (
        <AddressAndVenueStepPanel
          locationForm={locationForm}
          form={addressForm}
          fieldErrors={fieldErrors}
          pincodeStatus={pincodeStatus}
          malls={malls}
          mallsLoading={mallsLoading}
          customShoppingCentre={customShoppingCentre}
          shoppingCentreSaving={shoppingCentreSaving}
          mapValidation={{
            error: mapValidation.error,
            onError: (error) => setMapValidation({ isValid: false, error }),
            onValidationChange: ({ isValid, error }) => setMapValidation({ isValid, error: error || '' })
          }}
          onFieldChange={updateAddressField}
          onCustomShoppingCentreChange={updateCustomShoppingCentreField}
          onRegisterCustomShoppingCentre={handleRegisterCustomShoppingCentre}
        />
      );
    }

    if (stepId === 'store-code-review') {
      return (
        <StoreCodeAndReviewStepPanel
          draftData={draftData}
          locationForm={locationForm}
          detailsForm={detailsForm}
          addressForm={addressForm}
          reviewForm={reviewForm}
          fieldErrors={fieldErrors}
          onGenerateStoreCode={handleGenerateStoreCode}
          onReviewToggle={(checked) => {
            setReviewForm({ confirmed: checked });
            setFieldErrors((current) => ({
              ...current,
              reviewConfirmed: '',
              termsAccepted: '',
              privacyAccepted: ''
            }));
          }}
          onEditStep={handleStepNavigation}
          generatingCode={generatingCode}
        />
      );
    }

    return (
      <CompletionPanel
        summary={summary}
        storeCode={draftData?.reservedStoreCode || summary.storeCode}
        onCopyCode={copyStoreCode}
        onRegisterAnother={() => onNavigate('/register')}
        onGoToLogin={() => onNavigate('/login')}
      />
    );
  };

  return (
    <div className={`store-registration-shell${stepId === 'complete' ? ' is-complete' : ''}`}>
      <aside className="store-registration-sidebar">
        <div className="store-registration-sidebar-copy">
          {stepId === 'complete' ? (
            <>
              <h1>Registration complete.</h1>
              <p>
                Your store is now queued for approval. Keep the store code handy for the first sign in.
              </p>
            </>
          ) : (
            <>
              <h1>Turn first visits into regulars.</h1>
              <p>
                The operating system that runs your store and bring people back.
              </p>
            </>
          )}
        </div>

        <ol className="store-registration-stepper">
          {stepItems.map((step) => {
            const isActive = step.id === stepId || (stepId === 'complete' && step.id === 'store-code-review');
            const isComplete = stepIndexLookup[step.id] < currentStepIndex || (stepId === 'complete');
            const isStepClickable = isActive || isComplete;

            return (
              <li
                key={step.id}
                className={`store-registration-stepper-item${isActive ? ' is-active' : ''}${isComplete ? ' is-complete' : ''}`}
              >
                <button
                  type="button"
                  className="store-registration-stepper-trigger"
                  onClick={() => {
                    if (isStepClickable) {
                      handleStepNavigation(step.id);
                    }
                  }}
                  disabled={!isStepClickable}
                  aria-disabled={!isStepClickable}
                >
                  <span className="store-registration-stepper-badge">
                    {isComplete ? <Check size={18} aria-hidden="true" /> : step.number}
                  </span>
                  <span className="store-registration-stepper-copy">
                    <strong>{step.label}</strong>
                    <small>{step.caption}</small>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        <section className="store-registration-summary-card" aria-label="Registration summary">
          <div className="store-registration-summary-header">
            <div className="store-registration-summary-avatar">{summary.initials}</div>
            <div>
              <h2>{summary.storeName}</h2>
              <p>{summary.category}</p>
            </div>
          </div>

          <dl className="store-registration-summary-grid">
            <div>
              <dt>Location</dt>
              <dd>{summary.location}</dd>
            </div>
            <div>
              <dt>State</dt>
              <dd>{summary.state}</dd>
            </div>
            <div>
              <dt>Store code</dt>
              <dd>{summary.storeCode}</dd>
            </div>
            <div>
              <dt>Pincode</dt>
              <dd>{summary.pincode}</dd>
            </div>
          </dl>
        </section>

        <div className="store-registration-sidebar-meta">
          <p>
            <TimerReset size={16} aria-hidden="true" />
            <span>Setup takes about 3 minutes</span>
          </p>
        </div>

        <div className="store-registration-sidebar-support">
          <span>Need a hand?</span>
          <button type="button" onClick={() => onNavigate('/support')}>Talk to support</button>
        </div>
      </aside>

      <section className="store-registration-main">
        <header className="store-registration-main-header">
          <div className="store-registration-breadcrumbs">
            <span>/</span>
            <span>Onboarding</span>
            <span>/</span>
            <strong>{stepDefinition.label}</strong>
          </div>
          <div className="store-registration-progress">
            <span>{stepDefinition.progressLabel}</span>
            <div className="store-registration-progress-track" aria-hidden="true">
              <span
                className="store-registration-progress-fill"
                style={{ width: `${stepId === 'complete' ? 100 : Math.min(100, ((currentStepIndex + 1) / 4) * 100)}%` }}
              />
            </div>
          </div>
        </header>

        <div className="store-registration-main-body">
          <div className="store-registration-stage-header">
            <span className="store-registration-stage-pill">{stepDefinition.pill}</span>
            <h2>{stepDefinition.title}</h2>
            <p>{stepDefinition.description}</p>
          </div>

          <div className="store-registration-stage-content">
            {renderContent()}
          </div>
        </div>

        <footer className="store-registration-footer">
          <div className={`store-registration-footer-status is-${saveStatus}`}>
            {saveStatus === 'error' ? <CircleAlert size={16} aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
            <span>{saveMessage}</span>
          </div>

          {stepId === 'complete' ? (
            <div className="store-registration-footer-actions" />
          ) : (
            <div className="store-registration-footer-actions">
              {stepId !== 'location' ? (
                <button type="button" className="store-registration-button store-registration-button--ghost" onClick={handleBack}>
                  <ArrowLeft size={18} aria-hidden="true" />
                  <span>Back</span>
                </button>
              ) : null}
              <button
                type="button"
                className="store-registration-button store-registration-button--primary"
                onClick={handleContinue}
              >
                <span>{stepId === 'store-code-review' ? 'Create store' : 'Continue'}</span>
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            </div>
          )}
        </footer>
      </section>

      <PasswordModal
        open={passwordModal.open}
        values={passwordModal}
        onChange={updatePasswordModalField}
        onClose={() => {
          if (!submitting) {
            setPasswordModal(DEFAULT_PASSWORD_MODAL);
            setPasswordError('');
          }
        }}
        onSubmit={handleSubmitDraft}
        error={passwordError}
        submitting={submitting}
      />
    </div>
  );
};

export default StoreRegistrationOnboardingPage;
